<template>
  <button
    @click="toggleId"
    :class="{ active: isActive }"
    class="icon-wrapper"
    aria-label="Add to trip planner"
  >
    <span class="trip-planner-icon" v-html="config.tripPlannerIcon"></span>
  </button>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watchEffect } from "vue";
import { storageService } from "../services/storage.js";

import { useConfigStore } from "../stores";
const config = useConfigStore();

// Props
const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
});

// Reactive state
const isActive = ref(false);

/**
 * Toggles the presence of the current id in trip planner
 */
const toggleId = () => {
  const success = storageService.toggleTripPlannerItem(props.id);
  if (success) {
    isActive.value = storageService.isTripPlannerItem(props.id);
  }
};

/**
 * Update active state based on current storage
 */
const updateActiveState = () => {
  isActive.value = storageService.isTripPlannerItem(props.id);
};

// Storage change listener
let unsubscribe = null;

// Lifecycle hooks
onMounted(() => {
  updateActiveState();

  // Listen for storage changes
  unsubscribe = storageService.addListener("trip_planner_ids", updateActiveState);

  // Add event listeners for backward compatibility
  window.addEventListener("storage", updateActiveState);
  window.addEventListener("tripPlannerUpdate", updateActiveState);
});

onUnmounted(() => {
  // Remove storage listener
  if (unsubscribe) {
    unsubscribe();
  }

  // Remove event listeners
  window.removeEventListener("storage", updateActiveState);
  window.removeEventListener("tripPlannerUpdate", updateActiveState);
});

// Watch for changes to props.id
watchEffect(() => {
  if (props.id) {
    updateActiveState();
  }
});
</script>

<style>
.icon-wrapper {
  position: relative;
  padding: 0;
  border: 0;
  background: transparent;
  margin-left: var(--vwc-spacing-sm);
  display: flex;
  cursor: pointer;
}

.trip-planner-icon {
  display: flex;
  opacity: 1;
}

.trip-planner-icon svg {
  width: 20px;
  height: 20px;
}

.icon-wrapper.active .trip-planner-icon {
  opacity: 1;
}

.icon-wrapper.active .trip-planner-icon path {
  fill-opacity: 1;
}
</style>
